// Sound generation utility for UI interactions

const playSound = (frequency, duration, type = 'sine', volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.value = volume;

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

// Click sound for buttons - deep bass click without high frequencies
export const playClickSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Create a deep bass click
    const bufferSize = audioContext.sampleRate * 0.08; // 80ms for longer sound
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      // Two-stage envelope: sharp attack, gradual decay
      const t = i / bufferSize;
      const envelope = t < 0.1 ? 1 : Math.pow(1 - ((t - 0.1) / 0.9), 2);

      // Pure low-frequency tones only (no noise)
      const fundamental = Math.sin(2 * Math.PI * 150 * t); // 150Hz fundamental
      const harmonic = Math.sin(2 * Math.PI * 300 * t) * 0.5; // 300Hz harmonic
      data[i] = (fundamental + harmonic) * envelope * 0.4;
    }

    const source = audioContext.createBufferSource();
    source.buffer = buffer;

    // Low-pass filter to eliminate any high frequencies
    const filter = audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400; // Cut off everything above 400Hz
    filter.Q.value = 1;

    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.35;

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(audioContext.currentTime);
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

// Tab switch sound - deep bass click, slightly different from buttons
export const playTabSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Deep bass click for tabs
    const bufferSize = audioContext.sampleRate * 0.08;
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      const envelope = t < 0.1 ? 1 : Math.pow(1 - ((t - 0.1) / 0.9), 2);

      // Pure low-frequency tones only (no noise), slightly higher than button
      const fundamental = Math.sin(2 * Math.PI * 200 * t); // 200Hz fundamental
      const harmonic = Math.sin(2 * Math.PI * 400 * t) * 0.5; // 400Hz harmonic
      data[i] = (fundamental + harmonic) * envelope * 0.4;
    }

    const source = audioContext.createBufferSource();
    source.buffer = buffer;

    // Low-pass filter to eliminate any high frequencies
    const filter = audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500; // Cut off everything above 500Hz
    filter.Q.value = 1;

    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.3; // Slightly quieter for tabs

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(audioContext.currentTime);
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

// Action sound for primary buttons (start game, matchmaking) - higher pitched
export const playActionSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Higher-pitched bass click for action buttons
    const bufferSize = audioContext.sampleRate * 0.08;
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      const envelope = t < 0.1 ? 1 : Math.pow(1 - ((t - 0.1) / 0.9), 2);

      // Higher frequencies for action buttons (still warm, not harsh)
      const fundamental = Math.sin(2 * Math.PI * 300 * t); // 300Hz fundamental
      const harmonic = Math.sin(2 * Math.PI * 600 * t) * 0.5; // 600Hz harmonic
      data[i] = (fundamental + harmonic) * envelope * 0.4;
    }

    const source = audioContext.createBufferSource();
    source.buffer = buffer;

    // Low-pass filter to keep it warm
    const filter = audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 700; // Cut off everything above 700Hz
    filter.Q.value = 1;

    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.35;

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(audioContext.currentTime);
  } catch (error) {
    console.warn('Audio playback failed:', error);
  }
};

// Success/confirm sound - ascending notes
export const playSuccessSound = () => {
  playSound(523.25, 0.12, 'sine', 0.25); // C5
  setTimeout(() => playSound(659.25, 0.12, 'sine', 0.25), 40); // E5
  setTimeout(() => playSound(783.99, 0.15, 'sine', 0.2), 80); // G5
};

// Error/cancel sound - dissonant buzz
export const playErrorSound = () => {
  playSound(220, 0.12, 'sawtooth', 0.25); // A3
  setTimeout(() => playSound(233.08, 0.12, 'sawtooth', 0.25), 60); // A#3
};
